import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TableHead,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  Chip
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import { FrontendUser, LogType } from "../types";
import { RootState } from "../store";
import { useSelector } from "react-redux";
import { api } from "../utils/utils";
import { Colors } from "../Colors";
import { DateTime } from "luxon";
import { LogItem } from "../types";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { DateRange, PickersShortcutsItem } from "@mui/x-date-pickers-pro";
import DateRangePicker from "../components/date-range-picker/DateRangePicker";
import Select from "../components/select/Select";
import SignoutHeader from "../subcomponents/SignoutHeader";

const surfaces = {
  inactive: "rgba(255, 255, 255, 0.05)",
  inactiveHover: "rgba(255, 255, 255, 0.08)",
  textPrimary: "rgba(255, 255, 255, 0.9)",
  textSubtle: "rgba(255, 255, 255, 0.5)",
  transition: "all 0.15s ease",
  controlRadius: "6px"
};

const shortcutItems: PickersShortcutsItem<DateRange<DateTime>>[] = [
  {
    label: "Today",
    getValue: () => {
      const today = DateTime.local();
      return [today.startOf("day"), today.endOf("day")] as DateRange<DateTime>;
    }
  },
  {
    label: "Last 7 days",
    getValue: () => {
      const today = DateTime.local();
      return [today.minus({ days: 7 }), today] as DateRange<DateTime>;
    }
  },
  {
    label: "Last 30 days",
    getValue: () => {
      const today = DateTime.local();
      return [today.minus({ days: 30 }), today] as DateRange<DateTime>;
    }
  },
  {
    label: "Last 90 days",
    getValue: () => {
      const today = DateTime.local();
      return [today.minus({ days: 90 }), today] as DateRange<DateTime>;
    }
  }
];

const statusFilterOptions = Object.values(LogType)
  .filter((logType) => logType !== LogType.actionItem)
  .map((logType) => ({
    label: logType
      .replace(/-/g, " ")
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/\b\w/g, (str) => str.toUpperCase()),
    value: logType
  }));

const getLogTypeColor = (
  logType: LogType
): { background: string; text: string } => {
  switch (logType) {
    // Success
    case LogType.appointmentBooked:
    case LogType.planOfCareVisitBooked:
    case LogType.planOfCareScheduled:
    case LogType.updateAvailability:
    case LogType.initialVisitVisitBooked:
    case LogType.lateCancelFeePaid:
    case LogType.reminderConfirmed:
      return { background: "rgba(0, 172, 79, 0.12)", text: Colors.success };

    // Error
    case LogType.bookingFailed:
    case LogType.aiError:
    case LogType.textNotDelivered:
      return { background: "rgba(238, 38, 38, 0.12)", text: Colors.error };

    // Outbound communication
    case LogType.textSent:
    case LogType.initialVisitLinkSent:
    case LogType.planOfCareSent:
      return { background: "rgba(94, 120, 238, 0.12)", text: Colors.primary };

    // Inbound communication
    case LogType.textReceived:
      return { background: "rgba(72, 169, 153, 0.12)", text: Colors.tertiary };

    // Neutral / informational
    case LogType.viewAppointments:
    case LogType.joinedWaitlist:
      return { background: "rgba(252, 213, 84, 0.12)", text: Colors.warning };

    // Cancelled / muted
    case LogType.appointmentCancelled:
      return {
        background: "rgba(255, 255, 255, 0.06)",
        text: Colors.info
      };

    default:
      return { background: "rgba(252, 213, 84, 0.12)", text: Colors.warning };
  }
};

const Logs: React.FC = () => {
  const user: FrontendUser | null = useSelector(
    (state: RootState) => state.auth.user
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [logs, setLogs] = useState<LogItem[]>([]);
  const [filter, setFilter] = useState<LogType[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogItem[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange<DateTime<boolean>>>([
    DateTime.now().minus({ days: 30 }),
    DateTime.now()
  ]);

  const observer = useRef<IntersectionObserver>();
  const lastLogElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchMoreLogs();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const fetchLogs = async () => {
    if (!user?.token) return;

    try {
      setLoading(true);
      setLastEvaluatedKey(null);

      const params = new URLSearchParams({
        page_size: "20"
      });

      if (dateRange[0]) {
        params.append("start_date", dateRange[0].toISO() || "");
      }
      if (dateRange[1]) {
        params.append("end_date", dateRange[1].endOf("day").toISO() || "");
      }

      const response = await api.get(
        `/log-items?${params.toString()}`,
        user.token
      );

      const items = response.data.items || [];
      const nonActionItems = items.filter(
        (item: LogItem) => item.logType !== LogType.actionItem
      );
      const filteredItems =
        filter.length > 0
          ? nonActionItems.filter((item: LogItem) =>
              filter.includes(item.logType)
            )
          : nonActionItems;

      setLogs(nonActionItems);
      setHasMore(!!response.data.lastEvaluatedKey);
      setLastEvaluatedKey(response.data.lastEvaluatedKey);
      setFilteredLogs(filteredItems);
    } catch (error) {
      console.dir(error, { depth: null, colors: true });
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreLogs = async () => {
    if (!user?.token || !lastEvaluatedKey || !hasMore) return;

    try {
      setLoading(true);

      const params = new URLSearchParams({
        page_size: "20",
        last_evaluated_key: JSON.stringify(lastEvaluatedKey)
      });

      if (dateRange[0]) {
        params.append("start_date", dateRange[0].toISO() || "");
      }
      if (dateRange[1]) {
        params.append("end_date", dateRange[1].endOf("day").toISO() || "");
      }

      const response = await api.get(
        `/log-items?${params.toString()}`,
        user.token
      );

      const newItems = response.data.items || [];
      const nonActionItems = newItems.filter(
        (item: LogItem) => item.logType !== LogType.actionItem
      );
      const filteredItems =
        filter.length > 0
          ? nonActionItems.filter((item: LogItem) =>
              filter.includes(item.logType)
            )
          : nonActionItems;

      setLogs((prevLogs) => [...prevLogs, ...nonActionItems]);
      setHasMore(!!response.data.lastEvaluatedKey);
      setLastEvaluatedKey(response.data.lastEvaluatedKey);
      setFilteredLogs((prevLogs) => [...prevLogs, ...filteredItems]);
    } catch (error) {
      console.dir(error, { depth: null, colors: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (logs.length > 0) {
      const filteredItems =
        filter.length > 0
          ? logs.filter((item: LogItem) => filter.includes(item.logType))
          : logs;
      setFilteredLogs(filteredItems);
    }
  }, [filter, logs]);

  useEffect(() => {
    if (
      !loading &&
      hasMore &&
      logs.length > 0 &&
      filteredLogs.length === 0 &&
      lastEvaluatedKey
    ) {
      fetchMoreLogs();
    }
  }, [filteredLogs.length, hasMore, loading, logs.length, lastEvaluatedKey]);

  useEffect(() => {
    if (user?.token) {
      fetchLogs();
    }
  }, [user?.token, dateRange]);

  return (
    <Box
      sx={{
        marginTop: { xs: "75px", sm: 0 },
        color: "#FFF",
        minHeight: "100vh",
        pb: 4,
        "& .MuiOutlinedInput-root": { color: "#FFFFFF" },
        "& .MuiInputLabel-root": { color: Colors.info },
        "& .MuiInputBase-input": { color: "#FFFFFF" },
        "& .MuiSvgIcon-root": { color: "#FFF" },
        "& .MuiAutocomplete-paper": { backgroundColor: Colors.bg3, margin: 0 },
        "& .MuiAutocomplete-listbox": { backgroundColor: Colors.bg3 }
      }}
    >
      <SignoutHeader />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="700" sx={{ mb: 0.5 }}>
          Log Items
        </Typography>
        <Typography variant="body1" sx={{ color: Colors.info }}>
          Browse every interaction between Penciled and your patients.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          alignItems: "flex-end",
          mb: 3
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            width: "270px"
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: Colors.info
            }}
          >
            Filter by status
          </Typography>
          <Box
            sx={{
              "& > div > div:first-of-type": {
                backgroundColor: surfaces.inactive,
                border: `1px solid ${Colors.border}`,
                borderRadius: surfaces.controlRadius,
                minHeight: 40,
                padding: "0 12px",
                boxSizing: "border-box",
                color: surfaces.textPrimary,
                fontSize: "13px",
                transition: surfaces.transition,
                "&:hover": { backgroundColor: surfaces.inactiveHover }
              }
            }}
          >
            <Select
              multiple
              value={filter}
              onChange={(value) => {
                setFilter(value as LogType[]);
              }}
              options={statusFilterOptions}
              placeholder="All statuses"
              valueSelectedRender={(value) => (
                <span>
                  {!(value as LogType[]).length ||
                  (value as LogType[]).length === Object.values(LogType).length
                    ? "All statuses"
                    : `${(value as LogType[]).length} status${
                        (value as LogType[]).length === 1 ? "" : "es"
                      } selected`}
                </span>
              )}
              responsive
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            width: "270px"
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: Colors.info
            }}
          >
            Filter by date range
          </Typography>
          <Box
            sx={{
              "& > div": {
                backgroundColor: surfaces.inactive,
                border: `1px solid ${Colors.border}`,
                borderRadius: surfaces.controlRadius,
                minHeight: 40,
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                fontSize: "13px",
                transition: surfaces.transition,
                "&:hover": { backgroundColor: surfaces.inactiveHover }
              },
              "& > div > div:first-of-type": {
                flex: 1,
                padding: "0 4px 0 12px"
              },
              "& > div > div:first-of-type > span": {
                padding: 0
              }
            }}
          >
            <DateRangePicker
              dateRange={dateRange}
              onChange={setDateRange}
              shortcutItems={shortcutItems}
            />
          </Box>
        </Box>
      </Box>

      {filteredLogs.length === 0 && !loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: "120px"
          }}
        >
          <Typography variant="body1" sx={{ color: Colors.info }}>
            No logs found.
          </Typography>
        </Box>
      )}

      {filteredLogs.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderRadius: "12px",
            boxShadow: "none"
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
                    color: Colors.info,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    py: 2
                  }
                }}
              >
                <TableCell sx={{ width: "180px", pl: 3 }}>Time</TableCell>
                <TableCell sx={{ width: "180px" }}>Type</TableCell>
                <TableCell sx={{ width: "100px" }}>Patient</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.map((log, index) => {
                const color = getLogTypeColor(log.logType);
                return (
                  <TableRow
                    key={log.logItemId}
                    ref={
                      index === filteredLogs.length - 1
                        ? lastLogElementRef
                        : undefined
                    }
                    sx={{
                      "& td": {
                        borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                        py: 1.5
                      },
                      "&:last-of-type td": {
                        borderBottom: "none"
                      },
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.02)"
                      },
                      transition: "background-color 0.2s ease"
                    }}
                  >
                    <TableCell sx={{ pl: 3 }}>
                      <Tooltip
                        title={
                          log.createdAt
                            ? new Date(log.createdAt).toLocaleString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit"
                              })
                            : "Unknown time"
                        }
                        placement="top"
                      >
                        <Typography
                          sx={{ fontSize: "0.875rem", color: "#FFF" }}
                        >
                          {log.createdAt
                            ? formatDistanceToNow(new Date(log.createdAt), {
                                addSuffix: true
                              })
                            : "Unknown time"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.logType.replace(/-/g, " ")}
                        size="small"
                        sx={{
                          height: "22px",
                          backgroundColor: color.background,
                          color: color.text,
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          letterSpacing: "0.3px",
                          textTransform: "uppercase",
                          border: "none",
                          "& .MuiChip-label": {
                            px: 1.25
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/dashboard/patients/${log.patientId}?messageId=${
                          log.textThreadMessageId || ""
                        }`}
                        style={{ textDecoration: "none" }}
                      >
                        <Tooltip title="View chat">
                          <IconButton
                            size="small"
                            sx={{
                              color: Colors.info,
                              "&:hover": {
                                color: Colors.primary,
                                backgroundColor: "rgba(94, 120, 238, 0.08)"
                              }
                            }}
                          >
                            <ChatIcon sx={{ fontSize: "1.1rem" }} />
                          </IconButton>
                        </Tooltip>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          color: "#FFF"
                        }}
                      >
                        {log.logDescription}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
            gap: "1rem",
            marginTop: filteredLogs.length === 0 ? "8rem" : "2rem",
            marginBottom: "2rem"
          }}
        >
          <CircularProgress sx={{ color: Colors.info }} size={28} />
        </Box>
      )}
    </Box>
  );
};

export default Logs;
